import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import {
  useCityBySlug,
  useCityProducts,
  useTopStoresInCity,
  cityBySlugQueryOptions,
} from '@/hooks/cityPageHooks';
import ProductCard from '@/components/store/ProductCard';
import StoreCard from '@/components/StoreCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import AdSpace from '@/components/AdSpace';

export const Route = createFileRoute('/_regular/home/city/$citySlug')({
  component: CityPage,
  loader: ({ context: { queryClient }, params }) =>
    queryClient
      .ensureQueryData(cityBySlugQueryOptions(params.citySlug))
      .catch(() => null),
  head: ({ loaderData: city }) => {
    if (!city) return {}
    const description = city.description || `اكتشف أفضل المحلات والمتاجر في ${city.name}`
    return {
      meta: [
        { title: `متاجر ${city.name} | سكيكيمة` },
        { name: 'description', content: description },
        { property: 'og:title', content: city.name },
        { property: 'og:description', content: description },
      ],
    }
  },
});

function CityPage() {
  const { citySlug } = Route.useParams();
  const { data: city, isLoading: cityLoading } = useCityBySlug(citySlug);
  const [sortBy, setSortBy] = useState('-created_at');
  const { ref, inView } = useInView();
  const {
    data: productsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: productsLoading,
  } = useCityProducts(city?.id, sortBy);
  const { data: topStores = [], isLoading: storesLoading } = useTopStoresInCity(city?.id);
  const products = productsData?.pages.flatMap((page) => page.data) || [];

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (cityLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <Skeleton className="h-8 w-64 mb-4" />
      </div>
    );
  }
  if (!city) {
    return (
      <div className="container mx-auto py-8 px-4 text-center">
        المدينة غير موجودة
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4" dir="rtl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">{city.name}</h1>
        <p className="text-muted-foreground">
          استكشف المنتجات والمتاجر في هذه المدينة
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Main Content – Products */}
        <div className="lg:col-span-3">
          {/* Sort Controls */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">ترتيب حسب:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-[180px] flex-row-reverse justify-between">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  dir="rtl"
                  style={{ width: 'var(--radix-select-trigger-width)' }}
                  className="min-w-[var(--radix-select-trigger-width)]"
                >
                  <SelectItem value="-created_at">الأحدث</SelectItem>
                  <SelectItem value="-loves_count">الأكثر إعجاباً</SelectItem>
                  <SelectItem value="price">السعر: من الأقل</SelectItem>
                  <SelectItem value="-price">السعر: من الأعلى</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <span className="text-sm text-muted-foreground">
              {products.length} منتج
            </span>
          </div>

          {/* Products Grid */}
          {productsLoading && products.length === 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(6)].map((_, i) => (
                <Skeleton key={i} className="h-64 w-full" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">لا توجد منتجات في هذه المدينة بعد</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product, idx) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Load More Trigger */}
              <div ref={ref} className="h-10 mt-4" />
              {isFetchingNextPage && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              )}
            </>
          )}
        </div>

        {/* Right Sidebar – visible only on large screens */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="sticky top-20 space-y-6">
            <AdSpace variant="large" />
            <div className="bg-card rounded-lg border p-4">
              <h3 className="font-semibold mb-4">أفضل المتاجر في {city.name}</h3>
              {storesLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : topStores.length === 0 ? (
                <p className="text-sm text-muted-foreground">لا توجد متاجر بعد</p>
              ) : (
                <div className="space-y-3">
                  {topStores.map((store) => (
                    <StoreCard key={store.id} store={store} compact />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet inline ads – visible only below lg */}
      <div className="lg:hidden space-y-8 mt-8">
        <AdSpace variant="medium" />
        <div className="bg-card rounded-lg border p-4">
          <h3 className="font-semibold mb-4">أفضل المتاجر في {city.name}</h3>
          {storesLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : topStores.length === 0 ? (
            <p className="text-sm text-muted-foreground">لا توجد متاجر بعد</p>
          ) : (
            <div className="space-y-3">
              {topStores.map((store) => (
                <StoreCard key={store.id} store={store} compact />
              ))}
            </div>
          )}
        </div>
        <AdSpace variant="small" />
      </div>
    </div>
  );
}